import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { IdNameList } from "../../../../api/services/common/models";
import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
} from "../../../../components";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import {
  MAX_LENGTH_50,
  MDM_ACTIVITY_TYPE_KEY,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import { LabelValue } from "../../../../helpers/types/common";
import {
  arrayOfObjectsIncludesValue,
  buildActionMenuItems,
  checkFilterMatch,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  MdmActivitySearchResultFiltersTypes,
  MdmSearchActivityResultFilterForm,
  MdmSearchActivityResultFilterFormKey,
  MdmSearchActivityResultRowKey,
  MdmSearchActivityResultRowValue,
  ModalityType,
} from "../../modelsAndSchemas/models";
import { searchActivityResultSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { MdmActivitySearchResultWrapper } from "./MdmActivitySearchResults.style";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { usePermissions } from "../../../../hooks/usePermissions";

interface ProgramTemplateSearchResultProps {
  rows: MdmSearchActivityResultRowValue[]; //make is change here
  isSearchClick: boolean;
  pageId: string;
  setSelectedActivityId?: (id: string) => void;
  handleEditActivity?: (id: string) => void;
}

export const MdmActivitySearchResults: FC<ProgramTemplateSearchResultProps> = ({
  rows,
  isSearchClick,
  pageId,
  setSelectedActivityId,
  handleEditActivity,
}) => {
  const tableRef = React.useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  // updated siteCore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    MdmSearchActivityResultRowValue[]
  >([]);
  const [uniqueFilters, setUniqueFilters] =
    useState<MdmActivitySearchResultFiltersTypes>(() => ({
      programFrameWork: [],
      modality: [],
      type: [],
    }));

  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const {
    results,
    actionLabel,
    activityLabel,
    frameworkTypeLabel,
    allActivityLabel,
    modalityLabel,
    frameworkNameLabel,
    topicLabel,
    unitsList,
    selectedProgramFramework,
    selectedTopics,
    selectedModality,
    unitsLabel,
    modalityList,
  } = searchActivityResultSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(results, key);
  };

  const { control, reset, getValues } =
    useForm<MdmSearchActivityResultFilterForm>({
      mode: "all",
      defaultValues: {
        [MdmSearchActivityResultFilterFormKey.activityType]: "",
        [MdmSearchActivityResultFilterFormKey.programFrameWork]: "",
        [MdmSearchActivityResultFilterFormKey.modality]: "",
        [MdmSearchActivityResultFilterFormKey.topic]: "",
        [MdmSearchActivityResultFilterFormKey.units]: "",
        [MdmSearchActivityResultFilterFormKey.type]: "",
      },
    });

  const formValues = useWatch({
    control,
  });

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  useEffect(() => {
    resetPagination(true);
    reset();
  }, [rows]);

  const getUniqueFilters = (
    data: MdmSearchActivityResultRowValue[],
    key: string,
  ): LabelValue[] => {
    const flatValues: string[] = [];

    data.forEach((item) => {
      const value = item[key];

      if (Array.isArray(value)) {
        if (key === "programFrameWorkLst") {
          value.forEach((v) => v?.name && flatValues.push(v.name));
        } else if (key === "modalityLst") {
          value.forEach(
            (v) => v?.modalityName && flatValues.push(v.modalityName),
          );
        }
      }
    });

    return Array.from(new Set(flatValues)).map((v) => ({
      value: v,
      label: v,
    }));
  };

  // const programFrameworkValue = "Program Framework";
  // const projectFrameworkValue = "Project Framework";
  // Need to revisit this function if needed or not, once after confirmed with BA - Jagdish
  const getUniqueFrameWorkType = (data: MdmSearchActivityResultRowValue[]) => {
    const labelValues: LabelValue[] = [];
    data.forEach((item) => {
      if (item.type === MDM_ACTIVITY_TYPE_KEY.program) {
        labelValues.push({
          value: item.type,
          label: item.type,
        });
      } else if (item.type === MDM_ACTIVITY_TYPE_KEY.project) {
        labelValues.push({
          value: item.type,
          label: item.type,
        });
      }
    });

    const uniqueMap = new Map(labelValues.map((v) => [v.value, v]));
    return Array.from(uniqueMap.values());
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = [...rows];

      const modalityListDropdownValues = fetchDropdownValues(
        results,
        modalityList,
      );

      const modalityMap = new Map(
        modalityListDropdownValues.map(({ value, label }) => [value, label]),
      );

      localData.forEach((item) => {
        item.unitsValue = fetchValueByKeyOfDropdown(
          results,
          unitsList,
          item.units,
        );

        item.modalityLst = item.modalityLst.map((modality) => ({
          ...modality,
          modalityName:
            modalityMap.get(modality.modalityName) ?? modality.modalityName,
        }));
      });

      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [MdmSearchActivityResultFilterFormKey.programFrameWork]:
          getUniqueFilters(
            localData,
            MdmSearchActivityResultRowKey.programFrameWorkLst,
          ),
        [MdmSearchActivityResultFilterFormKey.modality]: getUniqueFilters(
          localData,
          MdmSearchActivityResultRowKey.modalityLst,
        ),
        [MdmSearchActivityResultFilterFormKey.type]:
          getUniqueFrameWorkType(localData),
      }));
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const { activityType, programFrameWork, modality, topic, units, type } =
        getValues();

      const filteredData = allUpdatedRows.filter((item) => {
        const activityTypeMatch = checkFilterMatch(
          activityType,
          item.activityType,
        );

        const typeMatch = checkFilterMatch(type, item.type);

        const programFrameWorkMatch = arrayOfObjectsIncludesValue(
          programFrameWork,
          item.programFrameWorkLst,
          "name",
        );

        const modalityMatch = arrayOfObjectsIncludesValue(
          modality,
          item.modalityLst,
          "modalityName",
        );

        const topicMatch = arrayOfObjectsIncludesValue(
          topic,
          item.topicLst,
          "name",
        );

        const unitsMatch = checkFilterMatch(units, item.units);

        return (
          activityTypeMatch &&
          programFrameWorkMatch &&
          modalityMatch &&
          topicMatch &&
          unitsMatch &&
          typeMatch
        );
      });
      setFilteredResults([...filteredData]);
      resetPagination();
    }
  }, [formValues]);

  const columns = [
    {
      id: MdmSearchActivityResultRowKey.activityType,
      label: getLabel(activityLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "activity-column",
      render: (row: RowProps) =>
        !row.activityType ? null : (
          <MuiLink
            onClick={() => setSelectedActivityId?.(row.id)}
            id={`${pageId}-view-activity`}
            data-testid="view-activity-link"
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link activity-column"
                text={row.activityType}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: MdmSearchActivityResultRowKey.type,
      label: getLabel(frameworkTypeLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "activity-column",
      render: (row: RowProps) => (
        <MuiTypography variant="h3">
          {row.type}
          {/* === MDM_SEARCH_ACTIVITY_SEARCH_TYPE_KEY.program
            ? programFrameworkValue
            : projectFrameworkValue} */}
        </MuiTypography>
      ),
    },
    {
      id: MdmSearchActivityResultRowKey.programFrameWorkLst,
      label: getLabel(frameworkNameLabel),
      isSortVisible: true,
      isEmptyValue: true,
      sortKeyName: "name",
      objectKeyName: MdmSearchActivityResultRowKey.programFrameWorkLst,
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={
            row.programFrameWorkLst?.map((item: IdNameList) => ({
              label: item.name,
              value: item.name,
            })) as ChipOptionsDataType[]
          }
          id={"selected-program-framework"}
          isEditable={false}
          headerLabel={getLabel(selectedProgramFramework)}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          isDialogTitleWithEllipsis
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: MdmSearchActivityResultRowKey.modalityLst,
      sortKeyName: "modalityName",
      objectKeyName: MdmSearchActivityResultRowKey.modalityLst,
      label: getLabel(modalityLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={
            row.modalityLst?.map((item: ModalityType) => ({
              label: item.modalityName,
              value: item.modalityId,
            })) as ChipOptionsDataType[]
          }
          id={"selected-modality"}
          isEditable={false}
          headerLabel={getLabel(selectedModality)}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: MdmSearchActivityResultRowKey.topicLst,
      label: getLabel(topicLabel),
      isSortVisible: true,
      isEmptyValue: true,
      sortKeyName: "name",
      objectKeyName: MdmSearchActivityResultRowKey.topicLst,
      className: "activity-column",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={
            row.topicLst?.map((item: IdNameList) => ({
              label: item.name,
              value: item.id,
            })) as ChipOptionsDataType[]
          }
          id={"selected-topics"}
          isEditable={false}
          headerLabel={getLabel(selectedTopics)}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
          wrapperClass="activity-column"
        />
      ),
    },
    {
      id: MdmSearchActivityResultRowKey.unitsValue,
      label: getLabel(unitsLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "units-cell",
    },
    {
      id: "action",
      label: getLabel(actionLabel),
      render: (row: RowProps) => {
        const menuItems = buildActionMenuItems(
          row,
          [
            {
              permissionKey: MODULE_PERMISSIONS.EDIT_MDM_ACTIVITY,
              create: (row) => ({
                onClick: () => handleEditActivity?.(row?.id),
                dataTestId: "edit",
                label: commonLabels.edit,
                ariaLabel: commonLabels.edit,
              }),
            },
          ],
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm = [
    <FormController
      control={control}
      name={MdmSearchActivityResultFilterFormKey.activityType}
      key={MdmSearchActivityResultFilterFormKey.activityType}
    >
      <MuiTextField
        id={`${pageId}-${MdmSearchActivityResultFilterFormKey.activityType}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmSearchActivityResultFilterFormKey.type}
      key={MdmSearchActivityResultFilterFormKey.type}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.type}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmSearchActivityResultFilterFormKey.type}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmSearchActivityResultFilterFormKey.programFrameWork}
      key={MdmSearchActivityResultFilterFormKey.programFrameWork}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.programFrameWork}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmSearchActivityResultFilterFormKey.programFrameWork}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmSearchActivityResultFilterFormKey.modality}
      key={MdmSearchActivityResultFilterFormKey.modality}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.modality}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmSearchActivityResultFilterFormKey.modality}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmSearchActivityResultFilterFormKey.topic}
      key={MdmSearchActivityResultFilterFormKey.topic}
    >
      <MuiTextField
        id={`${pageId}-${MdmSearchActivityResultFilterFormKey.topic}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmSearchActivityResultFilterFormKey.units}
      name={MdmSearchActivityResultFilterFormKey.units}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(results, unitsList)}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        onChange={() => {}}
        id={`${pageId}-${MdmSearchActivityResultFilterFormKey.units}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
  ];

  return (
    <MdmActivitySearchResultWrapper data-testid="mdm-activity-search-results">
      <MuiBox className="result-header">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {isSearchClick
            ? fetchComponentTitle(results)
            : getLabel(allActivityLabel)}
        </MuiTypography>
        {rows.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="selected-count">
            {rows.length}
          </MuiTypography>
        )}
      </MuiBox>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }}>
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            tableResults={[...filteredResults]}
            noDataInApi={isSearchClick}
            priorityResults={[]}
            isLastColumnSticky
          />
        </MuiGrid>
      </MuiGrid>
    </MdmActivitySearchResultWrapper>
  );
};
